import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import type { Database } from '@kit/supabase/database';

import type { DancerLocationContext } from './dancer-location.context';
import type {
  CreateLocationInput,
  DancerLocation,
} from './dancer-location.types';

/**
 * Best-effort mirror of the dancer's current location into
 * `profiles.{city, country_of_residence}`. Failures are logged but do not
 * surface to the caller — the `artist_locations` row already succeeded
 * and is the source of truth for distance preferences.
 *
 * Uses `country_code` (ISO alpha-2) as the country side. Skips if no
 * country_code was supplied (older Airtable rows can be missing it).
 */
export async function mirrorCurrentLocationToProfile(
  ctx: DancerLocationContext,
  userId: string,
  location: { city: string; country_code: string | null },
): Promise<void> {
  if (!location.country_code) return;

  const update: { city: string; country_of_residence: string } = {
    city: location.city,
    country_of_residence: location.country_code,
  };

  // Keyed on `id`, NOT `auth_user_id`. `dancer_locations.user_id` is in the
  // auth-uid space, which equals `profiles.id`: measured against production
  // 2026-09-04, all 426 location rows match a `profiles.id` and only 80
  // match an `auth_user_id`. So the previous filter mirrored nothing for
  // 346 of 426 dancers -- 81% -- because nothing has written
  // `profiles.auth_user_id` since 2026-05-02 (BAL-1041).
  //
  // Silently, because a PostgREST update matching zero rows returns
  // `error: null` and this inspected only `error`. The row count is now
  // checked, so the next such drift is logged rather than invisible.
  const { data, error } = await ctx.client
    .from('profiles')
    .update(update)
    .eq('id', userId)
    .select('id');

  if (!error && (data?.length ?? 0) === 0) {
    ctx.log('warn', 'Location mirror matched no profile row', {
      method: 'mirrorCurrentLocationToProfile',
      userId,
    });
  }
  if (error) {
    ctx.log('warn', 'Failed to mirror location to profiles row', {
      method: 'mirrorCurrentLocationToProfile',
      userId,
      error: error.message,
      errorCode: error.code,
    });
  }
}

/**
 * Upsert the dancer's single canonical current-residence row.
 *
 * Every current-residence edit surface (setup wizard About-You step,
 * `/edit` location section, contract-details address step) routes its
 * selected-city payload — built by `mapResidenceToCanonicalLocation` —
 * through here so the canonical `artist_locations.is_current` row carries
 * `place_id` + coordinates + `country_code`, not merely the free-text
 * `profiles.city` label. See
 * `docs/wip/active/WIP_making_dancer_location_canonical_2026_07_08.md`
 * (BAL-635).
 *
 * Unlike `addLocation`, this NEVER trips the date-overlap guard: a
 * residence edit REPLACES the one current row rather than appending a
 * date-ranged history entry, so re-editing a residence always succeeds.
 *
 * Coordinate/place_id downgrade guard: when the incoming payload lacks
 * coordinates or a place_id (e.g. the contract-details address step, which
 * resolves a mailing address without geometry), the existing values are
 * preserved rather than nulled — a coarser save must never erase a precise
 * pin a richer surface already captured (write-both, never leaving the
 * canonical row stale).
 *
 * Mirrors the result into `profiles.{city, country_of_residence}` (the
 * denormalized label the audition registry + ~10 legacy readers depend on).
 */
export async function upsertCurrentLocation(
  ctx: DancerLocationContext,
  userId: string,
  input: CreateLocationInput,
): Promise<Result<DancerLocation>> {
  const logContext = {
    method: 'upsertCurrentLocation',
    userId,
    city: input.city,
  };

  ctx.log('info', 'Upserting current location', logContext);

  try {
    // Validate date format (mirrors addLocation's guard).
    const startDate = new Date(input.start_date);
    if (isNaN(startDate.getTime())) {
      return Result.fail(
        ServiceError.validation('Invalid start_date format. Use YYYY-MM-DD'),
      );
    }

    // Load the existing canonical current row, if any. `is_current` is
    // treated as a singleton across the app; order + limit(1) keeps this
    // robust even if prior partial writes left more than one.
    const { data: existing, error: fetchError } = await ctx.client
      .from('artist_locations')
      .select('*')
      .eq('user_id', userId)
      .eq('is_current', true)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (fetchError) {
      ctx.log('error', 'Failed to load current location', {
        ...logContext,
        error: fetchError.message,
        errorCode: fetchError.code,
      });
      return Result.fail(ServiceError.database(fetchError.message, fetchError));
    }

    const hasPlaceId = !!(input.place_id && input.place_id.length > 0);

    if (existing) {
      // Update path — replace the label + dates in place; preserve a
      // precise pin / place_id when the incoming save omits them.
      const updatePayload: Database['public']['Tables']['artist_locations']['Update'] =
        {
          city: input.city,
          country: input.country,
          country_code: input.country_code ?? null,
          place_id: hasPlaceId ? input.place_id : existing.place_id,
          start_date: input.start_date,
          end_date: input.end_date ?? null,
          is_current: true,
          latitude: input.latitude ?? existing.latitude,
          longitude: input.longitude ?? existing.longitude,
        };

      const { data, error } = await ctx.client
        .from('artist_locations')
        .update(updatePayload)
        .eq('id', existing.id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        ctx.log('error', 'Failed to update current location', {
          ...logContext,
          error: error.message,
          errorCode: error.code,
        });
        return Result.fail(ServiceError.database(error.message, error));
      }

      // Collapse any drifted extra current rows to non-current so exactly
      // one is_current row remains.
      const { error: dedupeError } = await ctx.client
        .from('artist_locations')
        .update({ is_current: false })
        .eq('user_id', userId)
        .eq('is_current', true)
        .neq('id', existing.id);

      if (dedupeError) {
        ctx.log('warn', 'Failed to collapse drifted current locations', {
          ...logContext,
          error: dedupeError.message,
        });
      }

      await mirrorCurrentLocationToProfile(ctx, userId, {
        city: input.city,
        country_code: input.country_code ?? null,
      });

      return Result.ok(data);
    }

    // Insert path — first canonical current row for this dancer. Defensively
    // unset any stray is_current flags before inserting the new one.
    const { error: unsetError } = await ctx.client
      .from('artist_locations')
      .update({ is_current: false })
      .eq('user_id', userId)
      .eq('is_current', true);

    if (unsetError) {
      ctx.log('error', 'Failed to unset current locations', {
        ...logContext,
        error: unsetError.message,
      });
      return Result.fail(ServiceError.database(unsetError.message, unsetError));
    }

    const insertPayload = {
      user_id: userId,
      city: input.city,
      country: input.country,
      country_code: input.country_code ?? null,
      place_id: hasPlaceId ? input.place_id! : null,
      start_date: input.start_date,
      end_date: input.end_date ?? null,
      is_current: true,
      display_order: 0,
      latitude: input.latitude ?? null,
      longitude: input.longitude ?? null,
    };

    const { data, error } = await ctx.client
      .from('artist_locations')
      .insert(insertPayload)
      .select()
      .single();

    if (error) {
      ctx.log('error', 'Failed to insert current location', {
        ...logContext,
        error: error.message,
        errorCode: error.code,
      });
      return Result.fail(ServiceError.database(error.message, error));
    }

    await mirrorCurrentLocationToProfile(ctx, userId, {
      city: input.city,
      country_code: input.country_code ?? null,
    });

    return Result.ok(data);
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'upsertCurrentLocation',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}
